import { LocalStorageService } from '@mm/utils';
import _ from 'lodash';
import Actions from '../actions/actions';
import { setRequestDemoSnoozeDateActionCreator } from '../actions/viewActions';
import WidgetActions from '../actions/WidgetCRUDActions';
import { SlideOutMenuType, SlideoutState, ViewState } from '../interfaces/view';

const NESTED_MENU_TYPES: SlideOutMenuType[] = [
  'editor',
  'customerChatSlideoutEditor',
  'getemailsEditor',
  'createEditNativeIntegration',
  'qaEditor',
  'engagementEditor',
  'smsKeywordEditor',
  'igWelcomerEditor',
  'fbCommentGuardEditor',
  'shareLinksEditor',
  'igCommentGuardEditor',
  'igLiveEditor',
  'igStoryMentionEditor',
  'igStoryReactionEditor',
  'fbWelcomerEditor',
  'uploadContactListEditor',
  'smsBlasterEditor',
  'zapierIntegrationEditor',
  'connectionsEditor',
  'automationSoLDSettings',
  'editJSONWebhookIntegrationEditor',
  'mailchimpIntegrationEditor',
  'googleSheetIntegrationEditor',
  'automationSoLDSlackNotificationSettings',
  'businessHours',
  'ctmAdsEditor',
  'automationSoLDSettings',
  'automationSelectAudience',
  'automationSoLDSmsNotificationSettings',
  'automationSoLDEmailNotificationSettings',
  'automationSalesTeamNotifications',
  'automationEmailGenerate',
  'email',
];

// TODO: Remove all these -1's and replace with undefined for better type checking
const initialState: ViewState = {
  chatbotEditor: {
    activeChatbotId: -1,
    build: {
      activePageId: -1,
      activeFormFieldId: -1,
      activeKey: '',
    },
    confirmDeletePageGroup: {
      show: false,
      pageGroupId: -1,
      groupName: '',
    },
    confirmDeletePage: {
      show: false,
      pageId: -1,
      groupPages: [],
    },
    mainMenuSelected: false,
    selectedMessengerAd: null,
    selectedRssFeed: null,
    selectedLandingPage: null,
    selectedPromoBlaster: null,
    selectedAdConfig: null,
    selectedCtmAd: null,
  },
  confirmDeleteChatbot: {
    show: false,
    chatbotId: -1,
    name: '',
  },
  createBotModal: {
    show: false,
  },
  deleteErrorModal: {
    message: '',
    show: false,
  },
  slideoutMenu: [
    {
      show: false,
      menuType: undefined,
      menuProps: undefined,
    },
  ],
  checklistTooltip: {
    show: false,
  },
  activeDashboardPaginationPage: 1,
  requestDemoSnoozeDate: LocalStorageService.getItem('requestDemoSnoozeDate'),
  appNavigationHistory: [],
  landmineOpenId: undefined,
  visibleBetaSubNavs: {},
  widgetToFocus: undefined,

  widgetLoaderOrder: undefined,
};

const setMockWidgetLoader = (state: ViewState, widgetLoaderOrder) => {
  return {
    ...state,
    widgetLoaderOrder,
  };
};

type SetRequestDemoSnoozeDateActionCreator = ReturnType<
  typeof setRequestDemoSnoozeDateActionCreator
>;

const setRequestDemoSnoozeDate = (
  state: ViewState,
  action: SetRequestDemoSnoozeDateActionCreator,
) => {
  const { requestDemoSnoozeDate } = action;

  return {
    ...state,
    requestDemoSnoozeDate,
  };
};

const toggleConfirmDeletePageGroup = (state: ViewState, action, isShown) => ({
  ...state,
  chatbotEditor: {
    ...state.chatbotEditor,
    confirmDeletePageGroup: {
      ...state.chatbotEditor.confirmDeletePageGroup,
      show: isShown,
      pageGroupId: isShown ? action.pageGroupId : -1,
      groupName: isShown ? action.groupName : '',
    },
  },
});

const toggleConfirmDeletePage = (state: ViewState, action, isShown) => ({
  ...state,
  chatbotEditor: {
    ...state.chatbotEditor,
    confirmDeletePage: {
      ...state.chatbotEditor.confirmDeletePage,
      show: isShown,
      pageId: isShown ? action.pageId : -1,
      groupPages: isShown ? action.groupPages : [],
    },
  },
});

const toggleSelectMainMenu = (state: ViewState, isSelected) => {
  const newState = _.cloneDeep(state);
  newState.chatbotEditor.mainMenuSelected = isSelected;

  return newState;
};

const toggleSelectMessengerAds = (state: ViewState, action, isSelected) => {
  const newState = _.cloneDeep(state);
  newState.chatbotEditor.selectedMessengerAd = isSelected
    ? action.entity
    : null;

  return newState;
};

const toggleSelectRssFeeds = (state: ViewState, action, isSelected) => {
  const newState = _.cloneDeep(state);
  newState.chatbotEditor.selectedRssFeed = isSelected ? action.entity : null;

  return newState;
};

const toggleSelectLandingPage = (state: ViewState, action, isSelected) => {
  const newState = _.cloneDeep(state);
  newState.chatbotEditor.selectedLandingPage = isSelected
    ? action.entity
    : null;

  return newState;
};

const toggleSelectCtmAd = (state: ViewState, action, isSelected) => {
  const newState = _.cloneDeep(state);
  newState.chatbotEditor.selectedCtmAd = isSelected ? action.entity : null;

  return newState;
};

const toggleSelectAdConfig = (state: ViewState, action, isSelected) => {
  const newState = _.cloneDeep(state);
  newState.chatbotEditor.selectedAdConfig = isSelected ? action.entity : null;

  return newState;
};

const toggleDeleteErrorModal = (state: ViewState, action, isShown) => ({
  ...state,
  deleteErrorModal: {
    ...action.payload,
    show: isShown,
  },
});

const showSlideoutMenu = (
  state: ViewState,
  action: Exclude<SlideoutState, 'show'>,
  push: boolean,
) => {
  return {
    ...state,
    slideoutMenu: push
      ? [...state.slideoutMenu, { ...action, show: true }]
      : [
          {
            ...action,
            show: true,
          },
        ],
  };
};

const hideSlideoutMenu = (state: ViewState, push: boolean) => {
  const newState = _.cloneDeep(state);
  const update = newState.slideoutMenu.slice(
    0,
    newState.slideoutMenu.length - 1,
  );

  newState.slideoutMenu = push
    ? update
    : [
        {
          show: false,
          menuType: undefined,
          menuProps: {},
        },
      ];

  return newState;
};

const setDashboardPaginationPage = (state: ViewState, action) => ({
  ...state,
  activeDashboardPaginationPage: action.page,
});

const appendAppNavigationHistory = (state: ViewState, action) => {
  const HISTORY_LENGTH_TO_MAINTAIN = 10;
  const newHistory = [...state.appNavigationHistory];
  newHistory.unshift(action.location.pathname);

  return {
    ...state,
    appNavigationHistory: newHistory.slice(0, HISTORY_LENGTH_TO_MAINTAIN),
  };
};

const setLandmineOpenId = (state: ViewState, action) => ({
  ...state,
  landmineOpenId: action.landmineOpenId,
});

const setSuppressEmbeddedChatWidget = (state: ViewState, action) => ({
  ...state,
  suppressEmbeddedChatWidget:
    (state.suppressEmbeddedChatWidget || 0) +
    (action.suppressEmbeddedChatWidget ? 1 : -1),
});

const showBetaSubNav = (
  state: ViewState,
  { subNavName }: { subNavName: string },
) => ({
  ...state,
  visibleBetaSubNavs: { ...state.visibleBetaSubNavs, [subNavName]: true },
});

const hideBetaSubNav = (
  state: ViewState,
  { subNavName }: { subNavName: string },
) => ({
  ...state,
  visibleBetaSubNavs: _.omit(state.visibleBetaSubNavs, subNavName),
});

// tslint:disable-next-line:cyclomatic-complexity
export default function view(state = initialState, action) {
  switch (action.type) {
    case Actions.ACTIVATE_CHATBOT_EDITOR: {
      return {
        ...state,
        chatbotEditor: {
          ...state.chatbotEditor,
          activeChatbotId: action.chatbotId,
        },
      };
    }
    case Actions.ACTIVATE_PAGE_EDITOR: {
      return {
        ...state,
        chatbotEditor: {
          ...state.chatbotEditor,
          build: {
            ...state.chatbotEditor.build,
            activePageId: action.pageId,
          },
        },
      };
    }
    case Actions.DEACTIVATE_PAGE_EDITOR: {
      return {
        ...state,
        chatbotEditor: {
          ...state.chatbotEditor,
          build: {
            ...state.chatbotEditor.build,
            activePageId: -1,
          },
        },
      };
    }
    case Actions.SHOW_CONFIRM_DELETE_CHATBOT: {
      return {
        ...state,
        confirmDeleteChatbot: {
          show: true,
          chatbotId: action.chatbotId,
          name: action.name,
        },
      };
    }
    case Actions.HIDE_CONFIRM_DELETE_CHATBOT: {
      return {
        ...state,
        confirmDeleteChatbot: {
          show: false,
          chatbotId: -1,
          name: '',
        },
      };
    }
    case Actions.SHOW_CONFIRM_DELETE_PAGE_GROUP: {
      return toggleConfirmDeletePageGroup(state, action, true);
    }
    case Actions.HIDE_CONFIRM_DELETE_PAGE_GROUP: {
      return toggleConfirmDeletePageGroup(state, action, false);
    }
    case Actions.SHOW_CONFIRM_DELETE_PAGE: {
      return toggleConfirmDeletePage(state, action, true);
    }
    case Actions.HIDE_CONFIRM_DELETE_PAGE: {
      return toggleConfirmDeletePage(state, action, false);
    }
    case Actions.ACTIVATE_PAGE_EDITOR_FORM_FIELD: {
      return {
        ...state,
        chatbotEditor: {
          ...state.chatbotEditor,
          build: {
            ...state.chatbotEditor.build,
            activeFormFieldId: action.formFieldId,
          },
        },
      };
    }
    case Actions.ACTIVATE_PROPERTIES_PANE: {
      return {
        ...state,
        chatbotEditor: {
          ...state.chatbotEditor,
          build: {
            ...state.chatbotEditor.build,
            activeKey: action.activeKey,
          },
        },
      };
    }
    case Actions.SHOW_CREATE_BOT_MODAL: {
      const newState = _.cloneDeep(state);
      newState.createBotModal.show = true;

      return newState;
    }
    case Actions.HIDE_CREATE_BOT_MODAL: {
      const newState = _.cloneDeep(state);
      newState.createBotModal.show = false;

      return newState;
    }
    case Actions.SHOW_CHECKLIST_TOOLTIP: {
      const newState = _.cloneDeep(state);
      newState.checklistTooltip.show = true;

      return newState;
    }
    case Actions.HIDE_CHECKLIST_TOOLTIP: {
      const newState = _.cloneDeep(state);
      newState.checklistTooltip.show = false;

      return newState;
    }
    case Actions.UNSELECT_MAIN_MENU:
      return toggleSelectMainMenu(state, false);
    case Actions.SET_REQUEST_DEMO_SNOOZE_DATE:
      return setRequestDemoSnoozeDate(state, action);
    case Actions.SELECT_MAIN_MENU:
      return toggleSelectMainMenu(state, true);
    case Actions.MESSENGER_ADS_SELECT_AD:
      return toggleSelectMessengerAds(state, action, true);
    case Actions.MESSENGER_ADS_UNSELECT_AD:
      return toggleSelectMessengerAds(state, action, false);
    case Actions.RSS_FEEDS_SELECT_FEED:
      return toggleSelectRssFeeds(state, action, true);
    case Actions.RSS_FEEDS_UNSELECT_FEED:
      return toggleSelectRssFeeds(state, action, false);
    case Actions.LANDING_PAGES_SELECT_ENTITY:
      return toggleSelectLandingPage(state, action, true);
    case Actions.LANDING_PAGES_UNSELECT_ENTITY:
      return toggleSelectLandingPage(state, action, false);
    case Actions.CTM_ADS_SELECT_AD:
      return toggleSelectCtmAd(state, action, true);
    case Actions.CTM_ADS_UNSELECT_AD:
      return toggleSelectCtmAd(state, action, false);
    case Actions.AD_CONFIGS_SELECT_AD:
      return toggleSelectAdConfig(state, action, true);
    case Actions.AD_CONFIGS_UNSELECT_AD:
      return toggleSelectAdConfig(state, action, false);
    case Actions.SHOW_DELETE_ERROR_MODAL:
      return toggleDeleteErrorModal(state, action, true);
    case Actions.HIDE_DELETE_ERROR_MODAL:
      return toggleDeleteErrorModal(state, action, false);
    case Actions.SHOW_SLIDEOUT_MENU: {
      const push = state.slideoutMenu.some(
        menu => menu.menuType && NESTED_MENU_TYPES.includes(menu.menuType),
      );
      return showSlideoutMenu(state, action, push);
    }
    case Actions.HIDE_SLIDEOUT_MENU: {
      const push = state.slideoutMenu.some(
        menu => menu.menuType && NESTED_MENU_TYPES.includes(menu.menuType),
      );
      return hideSlideoutMenu(state, push);
    }
    case Actions.SET_DASHBOARD_PAGINATION_PAGE:
      return setDashboardPaginationPage(state, action);
    case Actions.APPEND_APP_NAVIGATION_HISTORY:
      return appendAppNavigationHistory(state, action);
    case Actions.SET_LANDMINE_OPEN_ID:
      return setLandmineOpenId(state, action);
    case Actions.SET_SUPPRESS_BOT_WIDGET:
      return setSuppressEmbeddedChatWidget(state, action);
    case Actions.VIEW_SHOW_BETA_SUBNAV:
      return showBetaSubNav(state, action);
    case Actions.VIEW_HIDE_BETA_SUBNAV:
      return hideBetaSubNav(state, action);
    case Actions.DISABLE_MAIN_SCROLL:
      return { ...state, disableMainScroll: true };
    case Actions.ENABLE_MAIN_SCROLL:
      return { ...state, disableMainScroll: false };
    case Actions.RESET:
      return initialState;
    case Actions.SET_WIDGET_TO_FOCUS:
      return {
        ...state,
        widgetToFocus: action.id,
      };
    case WidgetActions.createAction:
      return setMockWidgetLoader(state, action.entity?.sortOrder);
    case WidgetActions.createSuccessAction:
    case WidgetActions.createFailAction:
      return setMockWidgetLoader(state, undefined);
    default:
      return state;
  }
}
